import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, getDoc, where, getDocs } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth } from '../firebase';
import { useNotificationService } from '../services/notificationService';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import parse from 'html-react-parser';
import UserProfileModal from './UserProfileModal';
import Avatar from './Avatar';
import { useAuth } from '../hooks/useAuth';
import '../styles/global.css';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const Comunidad = () => {
    return (
        <Routes>
            <Route index element={<ComunidadHome />} />
            <Route path="post/:postId" element={<PostDetail />} />
        </Routes>
    );
};

// Configuración de Quill
const quillModules = {
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['clean']
    ],
};

const quillFormats = [
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet'
];

const ComunidadHome = () => {
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedType, setSelectedType] = useState('Todos');
    const [showCreatePost, setShowCreatePost] = useState(false);
    const [viewMode, setViewMode] = useState('list');
    const [sortOrder, setSortOrder] = useState('newest');
    const [currentUserProfile, setCurrentUserProfile] = useState(null);
    const navigate = useNavigate();

    // Estado para perfil de usuario
    const [selectedUserId, setSelectedUserId] = useState(null);
    const { user } = useAuth(); // Usar el hook useAuth

    const notificationService = useNotificationService(user);
    const postTypes = ['Todos', 'tip', 'duda', 'recurso', 'formato'];

    useEffect(() => {
        if (!user) return;

        // Cargar perfil de Firestore para el usuario actual
        const loadCurrentUserProfile = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    setCurrentUserProfile(userDoc.data());
                }
            } catch (error) {
                console.error("Error loading current user profile:", error);
            }
        };
        loadCurrentUserProfile();

        const categoriesQuery = query(collection(db, 'categories'), orderBy('name'));
        const unsubscribeCategories = onSnapshot(categoriesQuery, (snapshot) => {
            const categoriesData = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id
            }));
            setCategories(categoriesData);
        }, (error) => {
            console.error("Error fetching categories:", error);
        });

        const postsQuery = query(collection(db, 'posts'), orderBy('timestamp', 'desc'));
        const unsubscribePosts = onSnapshot(postsQuery, (snapshot) => {
            const postsData = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id
            }));
            setPosts(postsData);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching posts:", error);
            // Si el error es de permisos, es posible que el token no esté listo o App Check esté bloqueando
        });

        return () => {
            unsubscribeCategories();
            unsubscribePosts();
        };
    }, [user]); // Dependencia clave: user

    const filteredPosts = posts.filter(post => {
        const matchesSearch = searchText === '' ||
            post.title.toLowerCase().includes(searchText.toLowerCase()) ||
            (post.content && post.content.toLowerCase().includes(searchText.toLowerCase()));

        const matchesCategory = !selectedCategory || post.categoryId === selectedCategory;
        const matchesType = selectedType === 'Todos' || post.type === selectedType;

        return matchesSearch && matchesCategory && matchesType;
    }).sort((a, b) => {
        if (sortOrder === 'alphabetical') {
            return a.title.localeCompare(b.title);
        }
        // newest
        const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp || 0);
        const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp || 0);
        return dateB - dateA;
    });

    const handleLike = async (post) => {
        if (!auth.currentUser) return;
        const userId = auth.currentUser.uid;
        const postRef = doc(db, 'posts', post.id);
        const newLikes = post.likes?.includes(userId)
            ? post.likes.filter(id => id !== userId)
            : [...(post.likes || []), userId];
        await updateDoc(postRef, { likes: newLikes });
    };

    if (loading) {
        return <div className="loading">Cargando comunidad...</div>;
    }

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', color: 'var(--text-primary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h1 style={{ color: 'var(--accent)' }}>Comunidad Vortex</h1>
                <button
                    onClick={() => setShowCreatePost(true)}
                    className="btn-primary"
                    style={{ background: 'var(--accent)', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                    + Nuevo Post
                </button>
            </div>

            {/* Filters Bar */}
            <div className="filters-bar" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px', background: 'var(--bg-secondary)', padding: '15px', borderRadius: '12px' }}>
                <input
                    type="text"
                    placeholder="🔍 Buscar posts..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                />
                <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    style={{ padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                >
                    {postTypes.map(type => (
                        <option key={`type-${type}`} value={type}>
                            {type === 'Todos' ? 'Todos los tipos' : type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                    ))}
                </select>
                <select
                    value={selectedCategory || ''}
                    onChange={e => setSelectedCategory(e.target.value || null)}
                    style={{ padding: '10px', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                    <option value="">Todas las categorías</option>
                    {categories.map((c, index) => <option key={c.id || `cat-${index}`} value={c.id}>{c.icon} {c.name}</option>)}
                </select>
                <div style={{ display: 'flex' }}>
                    <button onClick={() => setViewMode('list')} style={{ opacity: viewMode === 'list' ? 1 : 0.5, background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>📋</button>
                    <button onClick={() => setViewMode('grid')} style={{ opacity: viewMode === 'grid' ? 1 : 0.5, background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>⣿</button>
                </div>
            </div>

            {/* Posts Grid/List */}
            <div style={{
                display: viewMode === 'grid' ? 'grid' : 'flex',
                flexDirection: viewMode === 'list' ? 'column' : 'initial',
                gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(300px, 1fr))' : 'none',
                gap: '20px'
            }}>
                {filteredPosts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>No hay posts que coincidan con tu búsqueda.</div>
                ) : (
                    filteredPosts.map((post, index) => {
                        if (!post.id) console.warn("Post with missing ID found at index:", index, post);
                        return (
                            <PostCard
                                key={post.id || `post-${index}`}
                                post={post}
                                categories={categories}
                                onPostClick={(id) => navigate(`/dashboard/comunidad/post/${id}`)}
                                onLike={() => handleLike(post)}
                                onUserClick={(uid) => setSelectedUserId(uid)}
                            />
                        );
                    })
                )}
            </div>

            {showCreatePost && (
                <CreatePostModal
                    categories={categories}
                    currentUserProfile={currentUserProfile}
                    onClose={() => setShowCreatePost(false)}
                    onPostCreated={() => setShowCreatePost(false)}
                />
            )}

            {selectedUserId && (
                <UserProfileModal
                    userId={selectedUserId}
                    onClose={() => setSelectedUserId(null)}
                />
            )}
        </div>
    );
};

const PostCard = ({ post, categories, onPostClick, onLike, onUserClick }) => {
    const category = categories.find(cat => cat.id === post.categoryId);
    const isLiked = post.likes?.includes(auth.currentUser?.uid);

    const handleDownload = (e, url, name) => {
        e.stopPropagation();
        const link = document.createElement('a');
        link.href = url;
        link.download = name || 'archivo_adjunto.json';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
  <div
  role="button"
  tabIndex={0}
  onClick={() => onPostClick(post.id)}
  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPostClick(post.id); } }}
            style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                padding: '20px',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                display: 'flex',
                flexDirection: 'column',
                gap: '15px'
            }}
            className="post-card"
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <div
                        onClick={(e) => { e.stopPropagation(); onUserClick(post.authorId); }}
                        style={{ cursor: 'pointer' }}
                    >
                        <Avatar src={post.authorAvatar} size={40} />
                    </div>
                    <div>
                        <div style={{ fontWeight: 'bold' }}>{post.authorNick}</div>
                        <div style={{ fontSize: '0.8rem', color: '#888' }}>
                            {post.timestamp?.toDate ? post.timestamp.toDate().toLocaleDateString() : 'Fecha desconocida'}
                        </div>
                    </div>
                </div>
                {category && (
                    <span style={{ background: category.color + '33', color: category.color, padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', border: `1px solid ${category.color}` }}>
                        {category.icon} {category.name}
                    </span>
                )}
            </div>

            <div>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem' }}>{post.title}</h3>
                <div style={{ fontSize: '0.95rem', color: '#ccc', maxHeight: '100px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                    {parse(post.content || '')}
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '10px' }}>
                <div style={{ display: 'flex', gap: '15px' }}>
                    <button onClick={(e) => { e.stopPropagation(); onLike(); }} style={{ background: 'none', border: 'none', color: isLiked ? '#ff6b6b' : '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        {isLiked ? '❤️' : '🤍'} {post.likes?.length || 0}
                    </button>
                    <span style={{ color: '#888', display: 'flex', alignItems: 'center', gap: '5px' }}>💬 {post.commentsCount || 0}</span>
                </div>
                {post.attachmentUrl && (
                    <button
                        onClick={(e) => handleDownload(e, post.attachmentUrl, post.attachmentName)}
                        style={{ background: 'rgba(255,255,255,0.1)', border: 'none', padding: '5px 10px', borderRadius: '4px', color: '#aaa', cursor: 'pointer', fontSize: '0.9rem' }}
                    >
                        📎 Archivo adjunto
                    </button>
                )}
            </div>
        </div>
    );
};

const CreatePostModal = ({ categories, currentUserProfile, onClose, onPostCreated }) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [type, setType] = useState('tip');
    const [categoryId, setCategoryId] = useState('');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!content.trim()) return;

        setUploading(true);
        try {
            let attachmentUrl = '';
            let attachmentName = '';

            if (file) {
                const storageRef = ref(getStorage(), `posts/${auth.currentUser.uid}/${Date.now()}_${file.name}`);
                await uploadBytes(storageRef, file);
                attachmentUrl = await getDownloadURL(storageRef);
                attachmentName = file.name;
            }

            await addDoc(collection(db, 'posts'), {
                title,
                content,
                type,
                categoryId,
                authorId: auth.currentUser.uid,
                authorNick: currentUserProfile?.nick || auth.currentUser.displayName || 'Usuario',
                authorAvatar: currentUserProfile?.avatar || auth.currentUser.photoURL || '👤',
                timestamp: new Date(),
                likes: [],
                commentsCount: 0,
                attachmentUrl,
                attachmentName
            });
            onPostCreated();
        } catch (error) {
            console.error("Error creating post:", error);
            alert("Error al crear el post.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div role="dialog" aria-modal="true" aria-labelledby="create-post-title" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#1e1e2e', padding: '25px', borderRadius: '16px', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #444' }}>
                <h2 id="create-post-title" style={{ color: 'white', marginTop: 0 }}>Crear Nuevo Post</h2>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <input
                        type="text"
                        placeholder="Título"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        required
                        style={{ padding: '12px', background: '#333', border: '1px solid #555', color: 'white', borderRadius: '8px' }}
                    />

                    <div className="rich-editor-container" style={{ background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
                        <ReactQuill
                            theme="snow"
                            value={content}
                            onChange={setContent}
                            modules={quillModules}
                            formats={quillFormats}
                            style={{ color: 'black' }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <select value={type} onChange={e => setType(e.target.value)} style={{ flex: 1, padding: '10px', background: '#333', color: 'white', border: '1px solid #555', borderRadius: '8px' }}>
                            <option value="tip">Tip</option>
                            <option value="duda">Duda</option>
                            <option value="recurso">Recurso</option>
                            <option value="formato">Formato</option>
                        </select>
                        <select value={categoryId} onChange={e => setCategoryId(e.target.value)} style={{ flex: 1, padding: '10px', background: '#333', color: 'white', border: '1px solid #555', borderRadius: '8px' }}>
                            <option value="">Seleccionar Categoría</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>

                    <div style={{ border: '2px dashed #444', padding: '20px', textAlign: 'center', borderRadius: '8px', cursor: 'pointer' }}>
                        <input
                            type="file"
                            id="file-upload"
                            accept=".json"
                            onChange={e => setFile(e.target.files[0])}
                            style={{ display: 'none' }}
                        />
                        <label htmlFor="file-upload" style={{ cursor: 'pointer', display: 'block', color: '#aaa' }}>
                            {file ? `📎 ${file.name}` : '📂 Adjuntar archivo JSON (Opcional)'}
                        </label>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button type="button" onClick={onClose} style={{ padding: '10px 20px', background: 'transparent', color: '#aaa', border: 'none', cursor: 'pointer' }}>Cancelar</button>
                        <button type="submit" disabled={uploading} style={{ padding: '10px 20px', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', opacity: uploading ? 0.7 : 1 }}>
                            {uploading ? 'Publicando...' : 'Publicar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const PostDetail = () => {
    const { postId } = useParams(); // Need to fix imports or use window.location if Route path has :postId
    // Actually in the main routing we used post/:postId, so useParams is better.
    // Let's import useParams. Wait, imports above have Route, Routes, useNavigate but NOT useParams.

    // Fallback if useParams not imported:
    const idFromUrl = window.location.pathname.split('/').pop();

    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newComment, setNewComment] = useState('');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [currentUserProfile, setCurrentUserProfile] = useState(null);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchPost = async () => {
            const docRef = doc(db, 'posts', idFromUrl);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setPost({ ...docSnap.data(), id: docSnap.id });
            }
            setLoading(false);
        };

        const commentsQuery = query(collection(db, 'posts', idFromUrl, 'comments'), orderBy('timestamp', 'asc'));
        const unsub = onSnapshot(commentsQuery, snap => {
            setComments(snap.docs.map(d => ({ ...d.data(), id: d.id })));
        });

        const loadCurrentUserProfile = async () => {
            if (!user) return;
            try {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    setCurrentUserProfile(userDoc.data());
                }
            } catch (error) {
                console.error("Error loading current user profile:", error);
            }
        };

        fetchPost();
        loadCurrentUserProfile();
        return () => unsub();
    }, [idFromUrl, user]);

    const handleComment = async () => {
        if (!newComment.trim()) return;
        setUploading(true);
        try {
            let attachmentUrl = '';
            let attachmentName = '';

            if (file) {
                const storageRef = ref(getStorage(), `posts/${idFromUrl}/comments/${Date.now()}_${file.name}`);
                await uploadBytes(storageRef, file);
                attachmentUrl = await getDownloadURL(storageRef);
                attachmentName = file.name;
            }

            await addDoc(collection(db, 'posts', idFromUrl, 'comments'), {
                content: newComment,
                authorId: auth.currentUser.uid,
                authorNick: currentUserProfile?.nick || auth.currentUser.displayName || 'Usuario',
                authorAvatar: currentUserProfile?.avatar || auth.currentUser.photoURL || '👤',
                timestamp: new Date(),
                attachmentUrl,
                attachmentName
            });

            await updateDoc(doc(db, 'posts', idFromUrl), {
                commentsCount: (post.commentsCount || 0) + 1
            });

            setNewComment('');
            setFile(null);
        } catch (e) {
            console.error(e);
        } finally {
            setUploading(false);
        }
    };

    if (loading) return <div>Cargando...</div>;
    if (!post) return <div>Post no encontrado</div>;

    return (
        <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto', color: 'var(--text-primary)' }}>
            <button onClick={() => navigate('/dashboard/comunidad')} style={{ marginBottom: '20px', background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer' }}>← Volver</button>

            <div style={{ background: 'var(--bg-secondary)', padding: '30px', borderRadius: '16px', marginBottom: '30px', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', mb: '20px', gap: '15px' }}>
                    <div
                        onClick={() => setSelectedUserId(post.authorId)}
                        style={{ cursor: 'pointer' }}
                    >
                        <Avatar src={post.authorAvatar} size={50} />
                    </div>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{post.title}</h1>
                        <span style={{ color: '#888' }}>Por {post.authorNick} • {post.timestamp?.toDate().toLocaleDateString()}</span>
                    </div>
                </div>

                <div className="post-content" style={{ marginTop: '20px', lineHeight: '1.6', fontSize: '1.1rem' }}>
                    {parse(post.content || '')}
                </div>

                {post.attachmentUrl && (
                    <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span>📎</span>
                        <span>{post.attachmentName || 'Archivo adjunto'}</span>
                        <a href={post.attachmentUrl} download target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto', color: 'var(--accent)' }}>Descargar</a>
                    </div>
                )}
            </div>

            <h3>Comentarios ({comments.length})</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '40px' }}>
                {comments.map((c, index) => (
                    <div key={c.id || `comment-${index}`} style={{ background: '#252535', padding: '15px', borderRadius: '12px', border: '1px solid #333' }}>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                            <div
                                onClick={() => setSelectedUserId(c.authorId)}
                                style={{ cursor: 'pointer' }}
                            >
                                <Avatar src={c.authorAvatar} size={30} />
                            </div>
                            <span style={{ fontWeight: 'bold' }}>{c.authorNick}</span>
                            <span style={{ color: '#666', fontSize: '0.8rem' }}>{c.timestamp?.toDate().toLocaleString()}</span>
                        </div>
                        <div style={{ marginLeft: '40px', color: '#ddd' }}>{parse(c.content || '')}</div>
                        {c.attachmentUrl && (
                            <div style={{ marginLeft: '40px', marginTop: '10px' }}>
                                <a href={c.attachmentUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: 'var(--accent)', textDecoration: 'none' }}>⬇️ Descargar {c.attachmentName || 'adjunto.json'}</a>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="add-comment" style={{ background: '#1e1e2e', padding: '20px', borderRadius: '16px', border: '1px solid #444' }}>
                <h4>Agregar comentario</h4>
                <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', marginBottom: '15px' }}>
                    <ReactQuill
                        theme="snow"
                        value={newComment}
                        onChange={setNewComment}
                        modules={quillModules}
                        style={{ color: 'black' }}
                        placeholder="Escribe tu respuesta aquí..."
                    />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <input type="file" id="comment-file" accept=".json" onChange={e => setFile(e.target.files[0])} style={{ display: 'none' }} />
                        <label htmlFor="comment-file" style={{ cursor: 'pointer', color: '#aaa', fontSize: '0.9rem' }}>
                            {file ? `📎 ${file.name}` : '📂 Adjuntar archivo JSON'}
                        </label>
                    </div>
                    <button
                        onClick={handleComment}
                        disabled={uploading || !newComment.trim()}
                        style={{ padding: '10px 25px', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', opacity: uploading ? 0.5 : 1 }}
                    >
                        {uploading ? 'Enviando...' : 'Comentar'}
                    </button>
                </div>
            </div>

            {selectedUserId && (
                <UserProfileModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
            )}
        </div>
    );
};

export default Comunidad;